import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import CustomToast from "app/components/common/CustomToast";
import UpgradePlanModal from "app/components/WebApp/GlobalNavBar/UpgradePlanModal";
import PaymentConnectionCard from "./PaymentConnectionCard";
import HowPaymentsWorkModal from "./HowPaymentsWorkModal";
import DisablePaymentsModal from "./DisablePaymentsModal";
import RemovePaymentSetupModal from "./RemovePaymentSetupModal";
import CancelPaymentSetupModal from "./CancelPaymentSetupModal";
import CantRemovePaymentModal from "./CantRemovePaymentModal";
import {
  fetchConnectAccount,
  createConnectAccount,
  createOnboardingLink,
  createLoginLink,
  setAccountDisabled,
  checkRemoval,
  removeConnectAccount,
} from "./slice";
import { PAYMENT_CONNECTION_SECTION_ID } from "./constants";

const openStripeTab = (url) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

const PaymentConnection = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    device: { isMobile },
    user: { profile },
  } = useApp();
  const { derivedState, account, actionStatus } = useSelector(
    (state) => state.paymentConnection
  );

  const [activeModal, setActiveModal] = useState(null); // how|disable|remove|cancel|cantRemove
  const [cantRemoveMessage, setCantRemoveMessage] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });

  const isFree = profile?.subscription?.tier === "Free";
  const actionLoading = actionStatus === "loading";

  const showToast = useCallback(
    (message, variant = "success") => setToast({ show: true, message, variant }),
    []
  );
  const closeModal = useCallback(() => setActiveModal(null), []);

  useInitialFetch(() => dispatch(fetchConnectAccount()));

  useInitialFetch(() => {
    const flag = searchParams.get("payment_onboarding");
    if (!flag) return;
    if (flag === "refresh") {
      dispatch(createOnboardingLink())
        .unwrap()
        .then((res) => {
          if (res?.onboarding_url) window.location.href = res.onboarding_url;
        })
        .catch(() => {});
    }
    searchParams.delete("payment_onboarding");
    setSearchParams(searchParams, { replace: true });
  });

  const startOnboarding = useCallback(
    (thunk) => {
      if (isFree) {
        setShowUpgrade(true);
        return;
      }
      dispatch(thunk())
        .unwrap()
        .then((res) => {
          if (res?.onboarding_url) window.location.href = res.onboarding_url;
        })
        .catch((err) => {
          if (err?.subscriptionRequired) setShowUpgrade(true);
          else showToast(err?.message || "Couldn't start setup", "error");
        });
    },
    [dispatch, isFree, showToast]
  );

  const onSetUp = useCallback(
    () => startOnboarding(createConnectAccount),
    [startOnboarding]
  );
  const onContinue = useCallback(
    () => startOnboarding(createOnboardingLink),
    [startOnboarding]
  );

  const openLoginLink = useCallback(() => {
    dispatch(createLoginLink())
      .unwrap()
      .then((url) => openStripeTab(url))
      .catch((err) =>
        showToast(err?.message || "Couldn't open Stripe dashboard", "error")
      );
  }, [dispatch, showToast]);

  const confirmDisable = useCallback(() => {
    dispatch(setAccountDisabled(true))
      .unwrap()
      .then(() => {
        closeModal();
        showToast(
          <>
            Payments disabled.
            <br />
            You can re-enable at any time.
          </>
        );
      })
      .catch((err) => showToast(err?.message || "Update failed", "error"));
  }, [dispatch, closeModal, showToast]);

  const onReenable = useCallback(() => {
    dispatch(setAccountDisabled(false))
      .unwrap()
      .then(() => showToast("Payments re-enabled."))
      .catch((err) => showToast(err?.message || "Update failed", "error"));
  }, [dispatch, showToast]);

  const showCantRemove = useCallback((message) => {
    setCantRemoveMessage(message);
    setActiveModal("cantRemove");
  }, []);

  const onRemove = useCallback(() => {
    dispatch(checkRemoval())
      .unwrap()
      .then((check) => {
        if (check?.can_remove === false) {
          showCantRemove(null);
        } else {
          setActiveModal("remove");
        }
      })
      .catch(() => setActiveModal("remove"));
  }, [dispatch, showCantRemove]);

  const confirmRemove = useCallback(() => {
    dispatch(removeConnectAccount())
      .unwrap()
      .then(() => {
        closeModal();
        showToast(
          <>
            Payment setup removed.
            <br />
            You can set up again at any time.
          </>
        );
      })
      .catch((err) => {
        if (err?.removalBlocked) {
          showCantRemove(err.message);
        } else {
          closeModal();
          showToast(err?.message || "Couldn't remove setup", "error");
        }
      });
  }, [dispatch, closeModal, showToast, showCantRemove]);

  const confirmCancel = useCallback(() => {
    dispatch(removeConnectAccount())
      .unwrap()
      .then(() => {
        closeModal();
        showToast(
          <>
            Payment setup canceled.
            <br />
            You can set up again at any time.
          </>
        );
      })
      .catch((err) => {
        closeModal();
        showToast(err?.message || "Couldn't cancel setup", "error");
      });
  }, [dispatch, closeModal, showToast]);

  const handlers = {
    onSetUp,
    onContinue,
    onCancel: () => setActiveModal("cancel"),
    onDisable: () => setActiveModal("disable"),
    onReenable,
    onRemove,
    onManage: openLoginLink,
    onViewIssue: openLoginLink,
    onHowItWorks: () => setActiveModal("how"),
  };

  return (
    <div id={PAYMENT_CONNECTION_SECTION_ID}>
      <PaymentConnectionCard
        state={derivedState}
        account={account}
        actionLoading={actionLoading}
        handlers={handlers}
      />

      <HowPaymentsWorkModal show={activeModal === "how"} onHide={closeModal} />
      <DisablePaymentsModal
        show={activeModal === "disable"}
        onHide={closeModal}
        onConfirm={confirmDisable}
        isLoading={actionLoading}
      />
      <RemovePaymentSetupModal
        show={activeModal === "remove"}
        onHide={closeModal}
        onConfirm={confirmRemove}
        isLoading={actionLoading}
      />
      <CancelPaymentSetupModal
        show={activeModal === "cancel"}
        onHide={closeModal}
        onConfirm={confirmCancel}
        isLoading={actionLoading}
      />
      <CantRemovePaymentModal
        show={activeModal === "cantRemove"}
        onHide={closeModal}
        onManage={openLoginLink}
        isLoading={actionLoading}
        message={cantRemoveMessage}
      />

      <UpgradePlanModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />

      <CustomToast
        show={toast.show}
        onClose={() => setToast((p) => ({ ...p, show: false }))}
        message={toast.message}
        variant={toast.variant}
        isMobile={isMobile}
        autohide
      />
    </div>
  );
};

export default PaymentConnection;
